/**
 * 数据传输安全改造：Axios 加解密 + 签名拦截器
 * 与后端 org.jeecg.config.crypto 包对应：
 * 1. 请求：AES 加密 body(payload)/GET 参数(encParams)，生成 HMAC-SHA256 签名，携带会话头
 * 2. 响应：解密 {"payload": "密文"} 格式的响应体
 * 3. 会话过期(4011)：自动重新协商密钥并重试一次
 *
 * 开关：.env 中 REACT_APP_CRYPTO_ENABLED=true 时启用（与后端 crypto.enabled 保持一致）
 */
import { aesEncrypt, aesDecrypt } from './aes';
import { generateSign, uuidV4 } from './sign';
import {
  ensureNegotiated,
  negotiateKey,
  getCryptoContext,
  clearCryptoContext,
  CRYPTO_PUBKEY_PATH,
  CRYPTO_NEGOTIATE_PATH,
} from './session';
import { getApiBase } from '../axios';

export const isCryptoEnabled = () => process.env.REACT_APP_CRYPTO_ENABLED === 'true';

/** 请求头名称（与后端 DecryptRequestFilter/SignatureInterceptor 保持一致） */
export const HEADER_SESSION_ID = 'X-Session-Id';
export const HEADER_TIMESTAMP = 'X-Timestamp';
export const HEADER_NONCE = 'X-Nonce';
export const HEADER_SIGNATURE = 'X-Signature';

/** 后端返回的会话过期错误码 */
const CODE_SESSION_EXPIRED = 4011;
const CODE_SESSION_MISSING = 4010;

/**
 * 加密排除清单（相对路径前缀，与后端 CryptoUrlWhitelist 保持一致）：
 * 密钥协商、登录认证、文件上传下载（二进制流）、在线表单/积木报表、
 * 字典及 Jeecg 原生 X-SIGN 签名接口、监控、API 文档、WebSocket、静态资源
 */
const EXCLUDE_URLS = [
  // 密钥协商
  CRYPTO_PUBKEY_PATH,
  CRYPTO_NEGOTIATE_PATH,
  // 登录认证
  '/sys/login',
  '/sys/logout',
  '/sys/phoneLogin',
  '/sys/mLogin',
  '/sys/randomImage',
  '/sys/checkCaptcha',
  '/sys/getCheckCode',
  '/sys/thirdLogin/',
  '/sys/cas/client/',
  '/sys/sms',
  '/sys/getEncryptedString',
  '/auth/2step-code',
  '/sys/user/checkOnlyUser',
  '/sys/user/register',
  '/sys/user/phoneVerification',
  '/sys/user/passwordChange',
  '/sys/getLoginQrcode',
  '/sys/getQrcodeToken',
  '/sys/checkAuth',
  // 文件上传下载（二进制流）
  '/sys/common/',
  // 在线表单(Online)/积木报表
  '/online/',
  '/jmreport/',
  '/jimureport/',
  // 字典及 Jeecg 原生 X-SIGN 签名接口（保持参数明文）
  '/sys/dict/',
  '/sys/api/',
  '/sys/category/',
  '/chartApp/',
  // 系统配置
  '/sys/config/querySysConfig',
  '/sys/permission/queryUserMenu',
  '/sys/duplicate/check',
  '/sys/annountCement/show/',
  // 监控
  '/actuator/',
  '/druid/',
  // API 文档
  '/doc.html',
  '/swagger',
  '/v2/',
  '/v3/',
  '/webjars/',
  // WebSocket
  '/websocket/',
  '/ws/',
  '/newsWebsocket/',
  '/vxeSocket/',
  // 静态资源
  '/static/',
  '/webapp/',
  '/assets/',
  '/generic/',
];

let axiosInstanceRef = null;

/**
 * 初始化：注册请求加密拦截器并保存 axios 实例引用（用于会话过期自动重试）
 * 在 src/utils/axios.js 中创建实例后调用
 */
export function setupCryptoInterceptor(instance) {
  axiosInstanceRef = instance;
  instance.interceptors.request.use(cryptoAxiosRequestHook, undefined);
  instance.interceptors.response.use(cryptoResponseHook, cryptoResponseErrorHook);
}

/**
 * 计算签名的相对路径：去掉 baseURL 前缀与查询串，
 * 与后端 SignatureInterceptor 的（请求 URI - contextPath）保持一致
 */
function getRelativePath(url) {
  const base = getApiBase();
  let path = url || '';
  if (base && path.startsWith(base)) {
    path = path.substring(base.length);
  }
  return path.split('?')[0];
}

function isExcluded(relativePath) {
  return EXCLUDE_URLS.some((u) => relativePath.startsWith(u));
}

/**
 * 请求拦截器：加密请求体/GET 参数 + HMAC 签名 + 会话请求头
 */
export async function cryptoAxiosRequestHook(config) {
  if (!isCryptoEnabled() || !config) {
    return config;
  }
  const relativePath = getRelativePath(config.url);
  if (isExcluded(relativePath)) {
    return config;
  }

  await ensureNegotiated();
  const ctx = getCryptoContext();
  if (!ctx) {
    return config;
  }

  const method = (config.method || 'get').toUpperCase();
  const isGet = method === 'GET';

  // 保存原始数据，供会话过期(4011)自动重试时还原
  config.__cryptoOriginal = { data: config.data, params: config.params };

  let bodyStr = '';
  if (!isGet) {
    if (config.data instanceof FormData || typeof config.data === 'string') {
      // multipart 表单/表单编码字符串不加密 body（后端对应跳过，签名 body 为空串）
      bodyStr = '';
    } else if (config.data) {
      // ① 加密请求体：{payload: "密文"}
      bodyStr = JSON.stringify(config.data);
      config.data = { payload: aesEncrypt(bodyStr, ctx.aesKey) };
    }
  } else if (config.params && typeof config.params !== 'string') {
    // ① 加密 GET 参数：{encParams: "密文"}
    config.params = { encParams: aesEncrypt(JSON.stringify(config.params), ctx.aesKey) };
  }

  // ② 生成 HMAC 签名（body 为加密前的明文 JSON 串，与后端解密后的 body 一致）
  const timestamp = Date.now().toString();
  const nonce = uuidV4();
  const sign = generateSign({
    method,
    path: relativePath,
    timestamp,
    nonce,
    body: bodyStr,
    signToken: ctx.signToken,
  });

  // ③ 请求头携带签名信息和会话 ID
  config.headers = config.headers || {};
  config.headers[HEADER_SESSION_ID] = ctx.sessionId;
  config.headers[HEADER_TIMESTAMP] = timestamp;
  config.headers[HEADER_NONCE] = nonce;
  config.headers[HEADER_SIGNATURE] = sign;

  return config;
}

/**
 * 响应拦截器：解密 {"payload": "密文"} 格式的响应体
 */
export function cryptoResponseHook(res) {
  if (!isCryptoEnabled()) {
    return res;
  }
  const data = res?.data;
  if (data && typeof data.payload === 'string') {
    const ctx = getCryptoContext();
    if (ctx) {
      try {
        res.data = JSON.parse(aesDecrypt(data.payload, ctx.aesKey));
      } catch (e) {
        console.error('[crypto] 响应解密失败', e);
      }
    }
  }
  return res;
}

/**
 * 响应错误拦截器：
 * 1. 解密加密的错误响应体，保证后续错误提示可读
 * 2. 会话过期(4011)/会话缺失(4010)时自动重新协商密钥并重试一次
 */
export async function cryptoResponseErrorHook(error) {
  if (!isCryptoEnabled()) {
    throw error;
  }
  const response = error?.response;
  let data = response?.data;

  // 解密错误响应体
  if (data && typeof data.payload === 'string') {
    const ctx = getCryptoContext();
    if (ctx) {
      try {
        response.data = data = JSON.parse(aesDecrypt(data.payload, ctx.aesKey));
      } catch (e) {
        // 解密失败保持原样
      }
    }
  }

  // 会话过期：清除会话、重新协商并重试一次
  const cfg = error?.config;
  if (
    data &&
    (data.code === CODE_SESSION_EXPIRED || data.code === CODE_SESSION_MISSING) &&
    cfg &&
    !cfg.__cryptoRetried
  ) {
    clearCryptoContext();
    await negotiateKey();
    const original = cfg.__cryptoOriginal || {};
    cfg.__cryptoRetried = true;
    const retryConfig = { ...cfg, data: original.data, params: original.params };
    const res = await axiosInstanceRef?.request(retryConfig);
    // 重试结果同样需要解密
    return cryptoResponseHook(res);
  }

  throw error;
}
